import {textToHtml} from '@frontapp/editor-html';
import {chain} from 'lodash';
import {DateTime} from 'luxon';
import React, {FC, useCallback, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {useToggle} from '../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {useInitialEffect} from '../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {isHtml} from '../../../../libs/shared/helpers/src/utilities/stringHelpers';
import {CheckBox} from '../../../../libs/shared/ui/src/components/booleanInputs/checkBox';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {PaletteColorsEnum} from '../../../../libs/shared/ui/src/styles/paletteModel';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {
  buildCalendarEventVersionRemindersToSave,
  buildDefaultShowAsForIsAllDay,
  calendarCanEditGlobalSettings,
  CalendarEventTimeTypesEnum,
  computeCalendarDateTime,
  computeCalendarStartEndDateTime,
  isEventEditable,
  isUnsavedDraftCalendarEventVersion,
} from '../../../../srcCommon/helpers/models/calendarEventVersionHelpers';
import {useLocalize} from '../../../../srcCommon/internationalization/internationalizationContext';
import {
  buildCalendarEventVersionColor,
  buildCalendarEventVersionStartEnd,
  StartEndData,
} from '../../../helpers/calendarHelpers';
import {TimeFormatsEnum} from '../../../helpers/dateTimeHelpers';
import {IntegrationFileAttachment} from '../../../helpers/integrationHelpers';
import {addOrganizerToVisualParticipants} from '../../../helpers/models/calendarEventHelpers';
import {isProviderConferenceType, tryFindConferenceJoinUrl} from '../../../helpers/models/conferenceHelpers';
import {
  isRecurringExceptionEvent,
  isRecurringMasterEvent,
  isRecurringSaveTypeRequired,
} from '../../../helpers/models/recurringEventHelpers';
import {useAppSelector} from '../../../helpers/reduxHelpers';
import {
  AttachmentModel,
  AttachmentStatusesEnum,
  CalendarEventVersionAttachmentModel,
  parseVisualContentType,
  RemoteAttachmentModel,
} from '../../../models/api/attachmentModel';
import {
  CalendarEventVersionModel,
  CalendarEventVersionReminder,
  CalendarEventVersionShowAsTypesEnum,
  CalendarEventVersionVisibilityTypesEnum,
} from '../../../models/api/calendarEventVersionModel';
import {CalendarProviderTypesEnum} from '../../../models/api/calendarModel';
import {CompanyModel} from '../../../models/api/companyModel';
import {ConferenceModel} from '../../../models/api/conferenceModel';
import {RecurrenceModel} from '../../../models/api/recurrenceModel';
import {SchedulingLinkModel} from '../../../models/api/schedulingLinkModel';
import {TeammateModel} from '../../../models/api/teammateModel';
import {VisualParticipantModel} from '../../../models/api/visualParticipantModel';
import {CalendarEventSaveData} from '../../../store/data/calendarEventVersions/calendarEventVersionsActions';
import {getHasCalendarAttachments} from '../../../store/featureFlags/featureFlagsSelectors';
import {CalendarEventData} from '../../../store/panels/calendars/calendarsPanelSelectors';
import {
  CalendarEventOverrides,
  ViewingNowCalendar,
} from '../../../store/panels/calendars/calendarsPanelState';
import {CalendarsPanelId, makeCalendarsPanelId} from '../../../store/panels/panelStateBase';
import {AttachmentsTransferProps, AttachmentsUploader} from '../../common/attachments/attachmentsUploader';
import {HtmlEditorBoxHandle} from '../../common/boxedInputs/htmlEditorBox';
import {DynamicScrollBoundaries} from '../../common/scroll/dynamicScrollBoundaries';
import {AttachmentBaseContainer} from '../../inboxes/viewer/activity/attachments/attachmentBaseContainer';
import {addBackgroundStyle} from '../../inboxes/viewer/activity/attachments/messageAttachment';
import {CalendarEventDetailRow} from '../viewer/calendarEventDetailRow';
import {CalendarAttachmentsButtonCoordinatorContainer} from './attachments/calendarAttachmentsButtonCoordinatorContainer';
import {SelectedCalendarData} from './calendarDropdownBox';
import {
  addConferenceToEventDescription,
  descriptionFromModelOrEditor,
  getRecurringSaveTypeOverride,
  hasAnyParticipantsExcludeGlobalOrganizer,
  maybeRenderInstantReschedulingCheckBox,
  maybeRenderRecurringSavePrompt,
  maybeRenderSaveWarning,
  renderCalendarSection,
  renderConferenceSection,
  renderDateSection,
  renderDescriptionSection,
  renderHeader,
  renderInstantReschedulingDialog,
  renderLocationSection,
  renderMeetingRoomsSection,
  renderMoreOptionsSection,
  renderParticipantsSection,
  renderRemindersSection,
  renderSummaryInput,
  StyledSectionIcon,
} from './calendarEventEditor';
import {OnCalendarEventEditorDoneArgs} from './calendarEventEditorContainer';
import {
  CalendarEventEditorModelChanges,
  computeCalendarEventEditorModelChanges,
  makeCalendarEventEditorDataModel,
} from './calendarEventEditorDataModel';
import {computeCalendarEventEditorFooterData} from './calendarEventEditorFooterData';

const calendarAttachmentMaxFileSizeInBytes = 3000000;

/*
 * Props.
 */

interface CalendarEventEditorProps {
  panelId: CalendarsPanelId;
  viewingNowCalendars: ReadonlyArray<ViewingNowCalendar>;
  calendarEventData: CalendarEventData;
  visualParticipants: ReadonlyArray<VisualParticipantModel>;
  instantReschedulingLink: SchedulingLinkModel | undefined;
  isEventOrganizer: boolean;
  timeFormat: TimeFormatsEnum;
  fetchOrCreateInstantReschedulingLinkAsync: FetchOrCreateInstantReschedulingLinkType;
  onBackClick?: Handler;
  onRequestClose?: Handler;
  onSaveClick: HandlerOf<CalendarEventSaveData>;
  onCalendarEventEditorDone: HandlerOf<OnCalendarEventEditorDoneArgs>;
  onFocusedEventOverridesChange: HandlerOf<CalendarEventOverrides> | undefined;
  onVisualParticipantsChange?: (
    oldVisualParticipants: ReadonlyArray<VisualParticipantModel>,
    newVisualParticipants: ReadonlyArray<VisualParticipantModel>,
  ) => void;
  onViewGuestAvailabilityClick?: HandlerOf<ReadonlyArray<VisualParticipantModel>> | undefined;
  masterCalendarEventVersion?: CalendarEventVersionModel;
  hasDefaultVideoConferencing: boolean;
  hasInstantReschedulingLinkFF: boolean;
  isInstantReschedulingEnabledPreference: boolean;
  user: TeammateModel;
  company: CompanyModel;
}

type FetchOrCreateInstantReschedulingLinkType = (calendarVersionId: number) => Promise<void>;

/*
 * Styles.
 */

const StyledLayoutDiv = styled.div`
  grid-template-rows: auto 1fr auto;
  display: grid;
  overflow: hidden;
  height: 100%;
`;

const StyledWrapperDiv = styled.div`
  padding: 2px 15px 0px 15px;
  display: grid;
  grid-auto-rows: min-content;
  grid-row-gap: 12px;
  overflow-y: scroll;
`;

const StyledFooterDiv = styled.div`
  position: sticky;
  bottom: 0px;
  background: ${(p) => p.theme.foreground};
  display: grid;
  grid-template-areas:
    'checkbox'
    'button';
`;

const StyledNotificationCheckboxDiv = styled.div`
  padding: 16px 16px 0 16px;
  grid-area: checkbox;

  display: flex;
`;

interface StyledNotificationCheckboxTextDivProps {
  $isDisabled: boolean;
}

const StyledNotificationCheckboxTextDiv = styled.div<StyledNotificationCheckboxTextDivProps>`
  padding-left: 8px;
  color: ${(p) => (p.$isDisabled ? p.theme.greys.shade60 : p.theme.greys.shade80)};
`;

const StyledNotificationCheckboxTooltipDiv = styled.div`
  padding-left: 4px;
`;

const StyledTooltipText = styled.div`
  font-weight: ${fontWeights.normal};
  font-size: ${fontSizes.verySmall};
  color: ${(p) => p.theme.greys.white};

  ul {
    padding: 0 12px 0 14px;
    margin: 0;
    line-height: 22px;
  }
`;

const StyledFooterButtonsDiv = styled.div`
  padding: 16px;

  grid-area: button;
  display: grid;
  grid-gap: 10px;
  grid-template-columns: auto auto;
  justify-content: end;
`;

interface StyledAttachmentDivProps {
  $hasError?: boolean;
}

export const StyledAttachmentDiv = styled.div<StyledAttachmentDivProps>`
  width: 158px;
  border: 1px solid ${(p) => p.theme.greys.shade40};
  border-radius: 8px;

  ${(p) => addBackgroundStyle(Boolean(p.$hasError), false)};
`;

interface StyledAttachmentsGridDivProps {
  $isEventEditor: boolean;
}

export const StyledAttachmentsGridDiv = styled.div<StyledAttachmentsGridDivProps>`
  display: grid;
  grid-template-columns: repeat(auto-fill, 160px);
  gap: 8px;

  margin-left: ${(p) => (p.$isEventEditor ? `26px` : ``)};
`;

/*
 * Component.
 */

enum RecurringEventSaveTypesEnum {
  ENTIRE_SERIES = 'entire_series',
  INSTANCE = 'instance',
}

interface SaveOptions {
  hasConfirmedSave?: boolean;
  recurringSaveType?: RecurringEventSaveTypesEnum;
}

export const CalendarEventEditorWithNotifyGuests: FC<CalendarEventEditorProps> = (props) => {
  const localize = useLocalize();
  const theme = useUserTheme();

  const {
    viewingNowCalendars,
    calendarEventData,
    isEventOrganizer,
    visualParticipants,
    instantReschedulingLink,
    hasInstantReschedulingLinkFF,
    isInstantReschedulingEnabledPreference,
    onSaveClick,
    fetchOrCreateInstantReschedulingLinkAsync,
    user,
    company,
  } = props;

  const {calendarEventVersion, calendarEvent, calendar, calendarVersion, channel, instanceStartDate} =
    calendarEventData;
  const channelType = channel ? channel.type : undefined;
  const instanceStartDateTime = instanceStartDate ? DateTime.fromMillis(instanceStartDate) : undefined;
  const {startDateTime, endDateTime} = computeCalendarStartEndDateTime(
    calendarEventVersion,
    instanceStartDateTime,
  );

  const [initialCalendarEventEditorDataModel] = useState(
    makeCalendarEventEditorDataModel(
      calendarEventVersion,
      calendarVersion,
      isInstantReschedulingEnabledPreference,
      channelType,
      startDateTime,
      endDateTime,
      visualParticipants,
    ),
  );

  const [calendarEventEditorDataModel, setCalendarEventEditorDataModel] = useState(
    initialCalendarEventEditorDataModel,
  );

  const hasCalendarAttachments = useAppSelector(getHasCalendarAttachments);

  // Functions that respond to changes to calendarEventEditorDataModel
  const onLocationChange = (updatedLocation: string) => {
    setCalendarEventEditorDataModel((prevDataModel) => ({
      ...prevDataModel,
      location: updatedLocation,
    }));
  };

  const onRecurrenceChange = (updatedRecurrence: RecurrenceModel | undefined) => {
    setCalendarEventEditorDataModel((prevDataModel) => ({
      ...prevDataModel,
      recurrence: updatedRecurrence,
    }));
  };

  const onSummaryChange = (updatedSummary: string) => {
    setCalendarEventEditorDataModel((prevDataModel) => ({
      ...prevDataModel,
      summary: updatedSummary,
    }));
  };

  const onNonResourceParticipantsChange = (
    updatedNonResourceParticipants: ReadonlyArray<VisualParticipantModel>,
  ) => {
    setCalendarEventEditorDataModel((prevDataModel) => ({
      ...prevDataModel,
      nonResourceParticipants: updatedNonResourceParticipants,
    }));
  };

  const onResourceParticipantsChange = (
    updatedResourceParticipants: ReadonlyArray<VisualParticipantModel>,
  ) => {
    setCalendarEventEditorDataModel((prevDataModel) => ({
      ...prevDataModel,
      resourceParticipants: updatedResourceParticipants,
    }));
  };

  const onInstantReschedulingEnabledChange = (updatedInstantReschedulingEnabled: boolean) => {
    setCalendarEventEditorDataModel((prevDataModel) => ({
      ...prevDataModel,
      isInstantReschedulingEnabled: updatedInstantReschedulingEnabled,
    }));
  };

  const onVisibilityChange = (updatedVisibility: CalendarEventVersionVisibilityTypesEnum) => {
    setCalendarEventEditorDataModel((prevDataModel) => ({
      ...prevDataModel,
      visibility: updatedVisibility,
    }));
  };

  const onAttachmentAdded = (attachment: CalendarEventVersionAttachmentModel) => {
    setCalendarEventEditorDataModel((prevDataModel) => ({
      ...prevDataModel,
      attachments: prevDataModel.attachments.concat(attachment),
    }));
  };

  const onAttachmentRemoved = (attachment: CalendarEventVersionAttachmentModel) => {
    if (attachment.status === AttachmentStatusesEnum.REMOTE) {
      onRemoteAttachmentRemoved(attachment.remotePath);
    } else {
      onUploadedAttachmentRemoved(attachment.uid);
    }
  };

  const onUploadedAttachmentRemoved = (uid: string) => {
    setCalendarEventEditorDataModel((prevDataModel) => ({
      ...prevDataModel,
      attachments: prevDataModel.attachments.filter((attachment) => attachment.uid !== uid),
    }));
  };

  const onRemoteAttachmentRemoved = (remotePath: string) => {
    setCalendarEventEditorDataModel((prevDataModel) => ({
      ...prevDataModel,
      attachments: prevDataModel.attachments.filter(
        (attachment) =>
          !(attachment.status === AttachmentStatusesEnum.REMOTE && attachment.remotePath === remotePath),
      ),
    }));
  };

  // Note: onRemindersChange is memoized here because is in the useEffect dependency array of CalendarRemindersEditor.
  // TODO: Refactor CalendarRemindersEditor's useEffect.
  const onRemindersChange = useCallback((updatedReminders: ReadonlyArray<CalendarEventVersionReminder>) => {
    setCalendarEventEditorDataModel((prevDataModel) => ({
      ...prevDataModel,
      reminders: updatedReminders,
    }));
  }, []);

  // An unsaved draft is true when we're editing an existing calendar event and false when creating a new event.
  const isUnsavedDraft = isUnsavedDraftCalendarEventVersion(calendarEventVersion);
  const attendeesCanInviteOthers =
    calendarEvent.attendeesCanInviteOthers && calendar.behaviors.supportsAttendeesCanInviteOthers;

  const [shouldShowSaveWarning, toggleSaveWarning] = useToggle(false);
  const [shouldShowRecurringSaveDialog, toggleRecurringSaveDialog] = useToggle(false);
  const [shouldNotifyGuests, toggleShouldNotifyGuests] = useToggle(true);

  const [saveOptions, setSaveOptions] = useState<SaveOptions>({
    hasConfirmedSave: false,
    recurringSaveType: undefined,
  });

  const [initialViewingNowCalendars] = useState<ReadonlyArray<ViewingNowCalendar>>(viewingNowCalendars);

  const calendarEventEditorModelChanges = computeCalendarEventEditorModelChanges({
    initialDataModel: initialCalendarEventEditorDataModel,
    currentDataModel: calendarEventEditorDataModel,
    isUnsavedDraft,
    userContactUid: user.contactUid,
  });

  const {didStartEndDataChange, didRecurrenceChange, guestChanges} = calendarEventEditorModelChanges;

  // Instant reschedule.
  useInitialEffect(() => {
    if (hasInstantReschedulingLinkFF && !instantReschedulingLink) {
      fetchOrCreateInstantReschedulingLinkAsync(calendarVersion.id);
    }
  });

  const [newLinkDialogPanelId, setNewLinkDialogPanelId] = useState<CalendarsPanelId>();
  const onNewLinkClick = () => setNewLinkDialogPanelId(makeCalendarsPanelId());
  const onNewLinkDialogClose = () => setNewLinkDialogPanelId(undefined);

  // Calendar.
  const [selectedCalendarData, setSelectedCalendarData] = useState({calendarVersion, calendar});
  const onSelectedCalendarDataChange = (newSelectedCalendarData: SelectedCalendarData) => {
    // If we've set a provider conference, but that conference type is not supported
    // by the currently selected calendar, clear the conference.
    if (
      conference &&
      isProviderConferenceType(conference.type) &&
      !newSelectedCalendarData.calendar.supportedProviderConferenceTypes.includes(conference.type)
    ) {
      setCalendarEventEditorDataModel((prevDataModel) => ({...prevDataModel, conference: undefined}));
    }

    setSelectedCalendarData(newSelectedCalendarData);
  };

  // Color.
  const [hasEditedColor, setHasEditedColor] = useState(false);

  const onColorChange = (newColor: PaletteColorsEnum) => {
    setCalendarEventEditorDataModel((prevDataModel) => ({...prevDataModel, color: newColor}));
    setHasEditedColor(true);
    if (props.onFocusedEventOverridesChange) {
      props.onFocusedEventOverridesChange({color: newColor});
    }
  };

  // Availability/Visibility.
  const [hasEditedShowAs, setHasEditedShowAs] = useState(false);
  const onShowAsChange = (showAsType: CalendarEventVersionShowAsTypesEnum) => {
    setHasEditedShowAs(true);
    setCalendarEventEditorDataModel((prevDataModel) => ({...prevDataModel, showAs: showAsType}));
  };

  // Start/End.
  const {startEndData} = calendarEventEditorDataModel;

  const onStartEndDataChange = (data: StartEndData) => {
    // When isAllDay changes for unsaved events where showAs hasn't been touched, update the showAs
    // to the default state
    const {isAllDay: wasAllDay} = startEndData;
    const {isAllDay} = data;
    if (isUnsavedDraft && !hasEditedShowAs && wasAllDay !== isAllDay) {
      onShowAsChange(buildDefaultShowAsForIsAllDay(isAllDay));
    }

    setCalendarEventEditorDataModel((prevDataModel) => ({...prevDataModel, startEndData: data}));

    if (props.onFocusedEventOverridesChange) {
      props.onFocusedEventOverridesChange({startEnd: buildCalendarEventVersionStartEnd(data)});
    }
  };

  // If the user changes the startEnd by dragging a region of a calendar grid, update our state.
  const {overrides} = calendarEventData;
  useEffect(() => {
    if (!(overrides && overrides.startEnd)) {
      return;
    }

    const newStartDateTime = computeCalendarDateTime(overrides.startEnd, CalendarEventTimeTypesEnum.START);
    const newEndDateTime = computeCalendarDateTime(overrides.startEnd, CalendarEventTimeTypesEnum.END);

    const {isAllDay} = overrides.startEnd;

    setCalendarEventEditorDataModel((prevDataModel) => ({
      ...prevDataModel,
      startEndData: {
        startDateTime: newStartDateTime,
        endDateTime: newEndDateTime,
        isAllDay,
      },
    }));
  }, [overrides]);

  // Recurrence

  // We disable editing a recurrence for:
  // - An existing series master event for which hasChangedStartEnd = true. Since
  //   we don't allow editing start/end for an entire series, we disable the
  //   recurrence whenever the start/end is changed.
  // - A series exception event.
  // - Non-organizers, since recurrence is a global property that providers
  //   don't allow participants to edit.
  const shouldDisableRecurrence =
    (!isUnsavedDraft && isRecurringMasterEvent(calendarEvent) && didStartEndDataChange) ||
    isRecurringExceptionEvent(calendarEvent) ||
    !isEventOrganizer;
  const {recurrence} = calendarEventEditorDataModel;

  const masterRecurrence = props.masterCalendarEventVersion
    ? props.masterCalendarEventVersion.recurrence
    : undefined;

  useEffect(() => {
    // If the recurrence is disabled, reset it to its initial value.
    if (shouldDisableRecurrence) {
      onRecurrenceChange(calendarEventVersion.recurrence);
    }
  }, [shouldDisableRecurrence, calendarEventVersion.recurrence]);

  // Description
  const editorRef = useRef<HtmlEditorBoxHandle>(null);
  const initialDescription = initialCalendarEventEditorDataModel.description || '';

  const onDescriptionChange = () => {
    setCalendarEventEditorDataModel((prevDataModel) => ({
      ...prevDataModel,
      description: descriptionFromModelOrEditor(calendarEventVersion, editorRef.current),
    }));
  };

  // Save button
  const {
    summary,
    location,
    conference,
    color,
    description,
    reminders,
    showAs,
    visibility,
    isInstantReschedulingEnabled,
    resourceParticipants,
    nonResourceParticipants,
    attachments,
  } = calendarEventEditorDataModel;

  const onSaveButtonClick = (saveOverrides?: SaveOptions) => {
    // Check if we need to prompt the user for a recurringSaveType.
    const recurringSaveType =
      (saveOverrides && saveOverrides.recurringSaveType) || saveOptions.recurringSaveType;

    const shouldPromptForRecurringSaveType = isRecurringSaveTypeRequired(calendarEvent) && !recurringSaveType;
    // Only show prompt if:
    // - we haven't changed start/end for now, since we don't support those series modifications yet.
    // - we haven't changed recurrence, since we don't support modifying both the series master
    // recurrence and creating an instance simulataneously.
    if (shouldPromptForRecurringSaveType && !didStartEndDataChange && !didRecurrenceChange) {
      toggleRecurringSaveDialog();
      return;
    }

    // Combine the options.
    const recurringSaveTypeOverride = shouldPromptForRecurringSaveType
      ? getRecurringSaveTypeOverride(didStartEndDataChange, didRecurrenceChange)
      : {};

    const options = {
      ...saveOptions,
      ...saveOverrides,
      ...recurringSaveTypeOverride,
    };

    // Show save warning if we're about to make changes that would only be reflected on our calendar.
    // Conditions are: 1) we're not the event organizer 2) we've made changes to the topic/date/location/description.
    if (
      calendarEventEditorModelChanges.didGlobalSettingsChange &&
      !isEventOrganizer &&
      !options.hasConfirmedSave
    ) {
      toggleSaveWarning();
      return;
    }

    // If we apply a change to the entire recurring series, we should preserve the startEnd of the master event.
    const newStartEnd =
      options.recurringSaveType === RecurringEventSaveTypesEnum.ENTIRE_SERIES
        ? {}
        : buildCalendarEventVersionStartEnd(startEndData);

    const {color: newColor, colorIsNop} = buildCalendarEventVersionColor(
      color,
      hasEditedColor,
      selectedCalendarData,
    );

    const newCalendarEventVersion = {
      ...calendarEventVersion,
      ...newStartEnd,
      includesTeammateReschedulingLink:
        isInstantReschedulingEnabled &&
        Boolean(instantReschedulingLink) &&
        hasAnyParticipantsExcludeGlobalOrganizer(nonResourceParticipants) &&
        isUnsavedDraft,
      summary,
      description,
      locations: location ? [{displayName: location}] : [],
      color: newColor,
      conference: conference && isProviderConferenceType(conference.type) ? conference : undefined,
      visibility,
      attachments,
      showAs,
      recurrence,
      reminders: buildCalendarEventVersionRemindersToSave(
        selectedCalendarData.calendarVersion.reminders,
        calendarEventVersion.reminders,
        reminders,
      ),
      calendarVersionIds: isUnsavedDraft
        ? [selectedCalendarData.calendarVersion.id]
        : calendarEventVersion.calendarVersionIds,
      isSaving: true,
    };

    // If we're creating a new event, we have to ensure that we include the organizer as a participant (they may not be attendee).
    const newVisualParticipants = [...resourceParticipants, ...nonResourceParticipants];
    const completeVisualParticipants = isUnsavedDraft
      ? addOrganizerToVisualParticipants(
          newVisualParticipants,
          selectedCalendarData.calendar.organizerAddress,
        )
      : newVisualParticipants;

    onSaveClick({
      newCalendarEventVersion,
      newCalendarEvent: calendarEvent,
      calendarVersion: selectedCalendarData.calendarVersion,
      visualParticipants: completeVisualParticipants,
      instanceStartDate,
      recurringSaveType: options.recurringSaveType,
      colorIsNop,
      shouldNotifyGuests,
    });
  };

  const onConferenceChange = (newConference: ConferenceModel | undefined) => {
    const editor = editorRef.current;
    if (!editor) {
      throw new Error('Missing calendar event description HTML editor box handle.');
    }

    if (!newConference) {
      // No cleanup needed if no conference or if the conference does not correspond to an integration.
      if (!conference || isProviderConferenceType(conference.type)) {
        setCalendarEventEditorDataModel((prevDataModel) => ({
          ...prevDataModel,
          conference: undefined,
        }));
        return;
      }

      // Remove the conference URL from the location, accounting for commas.
      const conferenceUrl = tryFindConferenceJoinUrl(conference);
      if (conferenceUrl) {
        const newLocation = location
          .replace(`, ${conferenceUrl}`, '')
          .replace(`${conferenceUrl}, `, '')
          .replace(conferenceUrl, '');

        onLocationChange(newLocation);
      }
      editor.setCleanHtml('');
      setCalendarEventEditorDataModel((prevDataModel) => ({
        ...prevDataModel,
        conference: undefined,
      }));
      return;
    }

    // Set the new conference and update the location/description if this conference
    // corresponds to an integration.
    setCalendarEventEditorDataModel((prevDataModel) => ({
      ...prevDataModel,
      conference: newConference,
    }));
    if (!isProviderConferenceType(newConference.type)) {
      const url = tryFindConferenceJoinUrl(newConference);
      if (url) {
        onLocationChange(location ? `${location}, ${url}` : url);
      }

      if (newConference.description) {
        const trimmedConferenceDescription = newConference.description.trim();
        const conferenceDescriptionHtml = isHtml(trimmedConferenceDescription)
          ? trimmedConferenceDescription
          : textToHtml(trimmedConferenceDescription);
        const eventDescriptionHtml = editor.isEmpty ? undefined : editor.cleanHtml;

        editor.setCleanHtml(addConferenceToEventDescription(eventDescriptionHtml, conferenceDescriptionHtml));
      }
    }
  };

  const canInviteParticipants =
    isEventEditable(
      selectedCalendarData.calendar,
      selectedCalendarData.calendarVersion,
      calendarEventVersion,
    ) &&
    (isEventOrganizer || attendeesCanInviteOthers);

  const canEditGlobalSettings = calendarCanEditGlobalSettings(
    selectedCalendarData.calendar,
    true,
    isEventOrganizer,
  );

  return (
    <AttachmentsUploader
      company={company}
      attachments={attachments}
      onAttachmentUploaded={onAttachmentAdded}
      sizeLimit={calendarAttachmentMaxFileSizeInBytes}
    >
      {(attachmentProps) => (
        <StyledLayoutDiv>
          {renderHeader(props, isUnsavedDraft, initialViewingNowCalendars)}
          <DynamicScrollBoundaries hasTopBoundary hasBottomBoundary color={theme.greys.shade30}>
            <StyledWrapperDiv>
              {renderSummaryInput(localize, props, summary, onSummaryChange, !canEditGlobalSettings)}
              {renderDateSection(
                startEndData,
                onStartEndDataChange,
                props.timeFormat,
                calendarEventVersion,
                instanceStartDate,
                masterRecurrence,
                recurrence,
                onRecurrenceChange,
                shouldDisableRecurrence,
                !canEditGlobalSettings,
              )}
              {renderParticipantsSection(
                props,
                initialCalendarEventEditorDataModel.nonResourceParticipants,
                nonResourceParticipants,
                onNonResourceParticipantsChange,
                canInviteParticipants,
                selectedCalendarData,
                isUnsavedDraft,
              )}
              {maybeRenderInstantReschedulingCheckBox(
                nonResourceParticipants,
                isInstantReschedulingEnabled,
                isUnsavedDraft,
                instantReschedulingLink,
                hasInstantReschedulingLinkFF,
                onInstantReschedulingEnabledChange,
                onNewLinkClick,
              )}
              {newLinkDialogPanelId &&
                renderInstantReschedulingDialog(
                  props,
                  newLinkDialogPanelId,
                  nonResourceParticipants,
                  instantReschedulingLink,
                  onNewLinkDialogClose,
                )}
              {renderLocationSection(localize, props, location, onLocationChange, !canEditGlobalSettings)}
              {renderMeetingRoomsSection(
                props,
                selectedCalendarData.calendarVersion,
                selectedCalendarData.calendar,
                resourceParticipants,
                onResourceParticipantsChange,
                location,
                onLocationChange,
                canInviteParticipants,
                startEndData,
              )}
              {/* Same calendar section as below, but putting it behind a FF for now */}
              {props.hasDefaultVideoConferencing &&
                renderCalendarSection(
                  selectedCalendarData,
                  onSelectedCalendarDataChange,
                  color,
                  onColorChange,
                  isUnsavedDraft,
                )}
              {canEditGlobalSettings &&
                renderConferenceSection(
                  selectedCalendarData.calendarVersion,
                  selectedCalendarData.calendar,
                  conference,
                  onConferenceChange,
                  startEndData,
                  summary,
                  isUnsavedDraft,
                )}
              {hasCalendarAttachments &&
                canEditGlobalSettings &&
                renderAttachmentsSection({
                  provider: selectedCalendarData.calendar.provider,
                  attachments: calendarEventEditorDataModel.attachments,
                  onAttachmentAdded,
                  onAttachmentRemoved,
                  attachmentProps,
                })}
              {canEditGlobalSettings &&
                renderDescriptionSection(editorRef, initialDescription, onDescriptionChange)}
              {!props.hasDefaultVideoConferencing &&
                renderCalendarSection(
                  selectedCalendarData,
                  onSelectedCalendarDataChange,
                  color,
                  onColorChange,
                  isUnsavedDraft,
                )}
              {renderRemindersSection(reminders, onRemindersChange)}
              {renderMoreOptionsSection(
                selectedCalendarData,
                showAs,
                onShowAsChange,
                visibility,
                onVisibilityChange,
              )}
              {maybeRenderRecurringSavePrompt(
                shouldShowRecurringSaveDialog,
                toggleRecurringSaveDialog,
                setSaveOptions,
                onSaveButtonClick,
              )}
              {maybeRenderSaveWarning(shouldShowSaveWarning, toggleSaveWarning, onSaveButtonClick)}
            </StyledWrapperDiv>
          </DynamicScrollBoundaries>
          {renderFooter({
            isEventOrganizer,
            onSaveButtonClick,
            props,
            initialViewingNowCalendars,
            toggleShouldNotifyGuests,
            shouldNotifyGuests,
            // Enable notification checkbox when 1) we add a new guest, 2) we remove a guest,
            // or 3) there are changes to global fields and there are guests to send updates to.
            isNotificationCheckboxEnabled:
              guestChanges.newGuestCount > 0 ||
              guestChanges.removedGuestCount > 0 ||
              (calendarEventEditorModelChanges.didGlobalFieldsChangeOmittingGuestChanges &&
                guestChanges.finalGuestCount > 0),
            isUnsavedDraft,
            calendarEventEditorModelChanges,
            provider: selectedCalendarData.calendar.provider,
            attachments: attachmentProps.attachments,
          })}
        </StyledLayoutDiv>
      )}
    </AttachmentsUploader>
  );
};

interface RenderAttachmentsSectionArgs {
  provider: CalendarProviderTypesEnum;
  attachments: ReadonlyArray<AttachmentModel>;
  onAttachmentAdded: HandlerOf<CalendarEventVersionAttachmentModel>;
  onAttachmentRemoved: HandlerOf<CalendarEventVersionAttachmentModel>;
  attachmentProps: AttachmentsTransferProps;
}

function renderAttachmentsSection({
  provider,
  attachments,
  onAttachmentAdded,
  onAttachmentRemoved,
  attachmentProps,
}: RenderAttachmentsSectionArgs) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <>
          <CalendarEventDetailRow
            iconContent={<StyledSectionIcon name="attachmentSmall" color={theme.greys.shade70} />}
            content={
              <CalendarAttachmentsButtonCoordinatorContainer
                onIntegrationAttachLinks={(integrationFileAttachments) => {
                  const newAttachments = integrationFileAttachments.map(createNewIntegrationAttachment);
                  newAttachments.map(onAttachmentAdded);
                }}
                provider={provider}
                onFileUpload={attachmentProps.onFileUpload}
              />
            }
          />
          {/* Google calendar attachents are remote, so we only show attachments stored in the data model.
              O365 attachments are uploaded, so we show attachments from the uploader, including draft attachments.  */}
          {renderAttachments(
            provider === CalendarProviderTypesEnum.OFFICE365 ? attachmentProps.attachments : attachments,
            onAttachmentRemoved,
            attachmentProps.onCancelAttachmentUploadClick,
          )}
        </>
      )}
    </UserThemeConsumer>
  );
}

function renderAttachments(
  attachments: ReadonlyArray<AttachmentModel>,
  onAttachmentRemoved: HandlerOf<CalendarEventVersionAttachmentModel>,
  onCancelAttachmentUploadClick: HandlerOf<string>,
) {
  if (attachments.length === 0) {
    return null;
  }

  return (
    <StyledAttachmentsGridDiv $isEventEditor>
      {chain(attachments)
        .sortBy((attachment) => attachment.name.toLowerCase())
        .map((attachment) => {
          // This is a temporary limit on attachments uploading from computer.
          // If an attachment is over 3MB, we will stop the upload and show it with an error state.
          const shouldShowUploadError =
            attachment.size > calendarAttachmentMaxFileSizeInBytes &&
            attachment.status === AttachmentStatusesEnum.DRAFT;

          return (
            <StyledAttachmentDiv $hasError={shouldShowUploadError}>
              <AttachmentBaseContainer
                attachment={attachment}
                canBeRemoved
                onRemoveClick={() => {
                  if (
                    attachment.status === AttachmentStatusesEnum.UPLOADED ||
                    attachment.status === AttachmentStatusesEnum.REMOTE
                  ) {
                    onAttachmentRemoved(attachment);
                  }
                }}
                onCancelUploadClick={() => {
                  onCancelAttachmentUploadClick(attachment.cid);
                }}
                hasError={shouldShowUploadError}
                customErrorMessage={<Trans id="calendar-attachment-sizeerror">Exceeds 3MB limit</Trans>}
                shouldDisplayErrorMessageInTooltip
              />
            </StyledAttachmentDiv>
          );
        })
        .value()}
    </StyledAttachmentsGridDiv>
  );
}

function createNewIntegrationAttachment(attachment: IntegrationFileAttachment): RemoteAttachmentModel {
  const {name, url, mimeType} = attachment;
  return {
    uid: '',
    linkId: undefined,
    name,
    size: 0,
    contentType: '',
    visualContentType: parseVisualContentType(mimeType ? mimeType.toLowerCase() : ''),
    cid: '',
    isInline: false,
    attachedAt: undefined,
    giphyId: undefined,
    hasThumbnail: false,
    url: '',
    status: AttachmentStatusesEnum.REMOTE,
    remotePath: url,
    remoteMimeType: mimeType || '',
  };
}

interface RenderFooterParams {
  onSaveButtonClick: Handler;
  props: CalendarEventEditorProps;
  initialViewingNowCalendars: ReadonlyArray<ViewingNowCalendar>;
  toggleShouldNotifyGuests: HandlerOf<boolean>;
  shouldNotifyGuests: boolean;
  isNotificationCheckboxEnabled: boolean;
  isUnsavedDraft: boolean;
  calendarEventEditorModelChanges: CalendarEventEditorModelChanges;
  provider: CalendarProviderTypesEnum;
  isEventOrganizer: boolean;
  attachments: ReadonlyArray<AttachmentModel>;
}

function renderFooter({
  onSaveButtonClick,
  props,
  initialViewingNowCalendars,
  toggleShouldNotifyGuests,
  shouldNotifyGuests,
  isNotificationCheckboxEnabled,
  isUnsavedDraft,
  calendarEventEditorModelChanges,
  provider,
  isEventOrganizer,
  attachments,
}: RenderFooterParams) {
  const {isSaveEnabled, didGlobalFieldsChangeOmittingGuestChanges, guestChanges} =
    calendarEventEditorModelChanges;
  const {checkboxText, tooltip} = computeCalendarEventEditorFooterData({
    guestChanges,
    didGlobalFieldsChangeOmittingGuestChanges,
    isUnsavedDraft,
  });

  const isCheckboxDisabled = !isNotificationCheckboxEnabled;
  const isChecked = () => {
    // A disabled checkbox will never be checked.
    if (isCheckboxDisabled) {
      return false;
    }
    return shouldNotifyGuests;
  };

  const isAnyAttachmentDraft = attachments.some(({status}) => status === AttachmentStatusesEnum.DRAFT);
  const isAnyDraftAttachmentTooLarge = attachments.some(
    (attachment) =>
      attachment.size > calendarAttachmentMaxFileSizeInBytes &&
      attachment.status === AttachmentStatusesEnum.DRAFT,
  );
  const isSaveDisabled = !isSaveEnabled || isAnyAttachmentDraft || isAnyDraftAttachmentTooLarge;

  return (
    <StyledFooterDiv>
      {provider === CalendarProviderTypesEnum.GOOGLE && isEventOrganizer && (
        <StyledNotificationCheckboxDiv>
          <CheckBox
            onChange={toggleShouldNotifyGuests}
            isChecked={isChecked()}
            isDisabled={isCheckboxDisabled}
          />
          <StyledNotificationCheckboxTextDiv $isDisabled={isCheckboxDisabled}>
            {checkboxText}
          </StyledNotificationCheckboxTextDiv>
          {tooltip && (
            <UserThemeConsumer>
              {(theme) => (
                <StyledNotificationCheckboxTooltipDiv>
                  <TooltipCoordinator
                    render={() => <StyledTooltipText>{tooltip}</StyledTooltipText>}
                    iconColor={theme.greys.shade70}
                    iconHoverColor={theme.greys.shade80}
                    placement="top"
                  />
                </StyledNotificationCheckboxTooltipDiv>
              )}
            </UserThemeConsumer>
          )}
        </StyledNotificationCheckboxDiv>
      )}
      <StyledFooterButtonsDiv>
        <Button
          size={VisualSizesEnum.MEDIUM}
          variant={ButtonVariantsEnum.SECONDARY}
          onClick={() =>
            props.onCalendarEventEditorDone({
              viewingNowCalendarsToResetTo: initialViewingNowCalendars,
              finalHandler: props.onBackClick || props.onRequestClose,
            })
          }
        >
          Cancel
        </Button>
        <Button
          size={VisualSizesEnum.MEDIUM}
          variant={ButtonVariantsEnum.PRIMARY}
          onClick={() => onSaveButtonClick()}
          isDisabled={isSaveDisabled}
        >
          Save
        </Button>
      </StyledFooterButtonsDiv>
    </StyledFooterDiv>
  );
}
